import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { StaffFeesOverdueReportService } from '../services/staff-fees-overdue-report.service';
import { StudentFeeOverdueRecord } from '../services/staff-fees-pending-report.service';
import { StaffFeesReportOverdueStudentHistoryNavState } from '../fees-report-overdue-student-history/staff-fees-report-overdue-student-history.component';
import { StaffFeesReportNavState } from '../fees-report/staff-fees-report.component';
import { StaffSessionService } from '../services/staff-session.service';

export interface StaffFeesReportOverdueStudentsNavState {
  classNumber: string;
  classDivision: string;
}

// Parallel to StaffFeesReportStudentsComponent, but for the "Fees Overdue
// Report" flow (getPendingStudentList — see apiConstant.ts naming note).
@Component({
  selector: 'app-staff-fees-report-overdue-students',
  templateUrl: './staff-fees-report-overdue-students.component.html',
  styleUrls: ['../shared/staff-common.css', '../fees-report-students/staff-fees-report-students.component.css']
})
export class StaffFeesReportOverdueStudentsComponent implements OnInit {
  staffId: number | string | null = null;
  currencySymbol = '';

  classId: string = '';
  className = '';
  classNumber = '';
  classDivision = '';

  activeTab: 'active' | 'deactivated' = 'active';
  fetchingStudentListData = true;

  activatedStudentData: StudentFeeOverdueRecord[] = [];
  deactivatedStudentData: StudentFeeOverdueRecord[] = [];
  totalActiveStudentCount = 0;
  totalDeactivateStudentCount = 0;
  totalActiveStudentPendingAmount = 0;
  totalPendingAmount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffFeesOverdueReportService: StaffFeesOverdueReportService,
    private staffSession: StaffSessionService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';

    this.classId = this.route.snapshot.paramMap.get('classId') || '';
    const state = history.state as StaffFeesReportOverdueStudentsNavState;
    this.classNumber = state?.classNumber || '';
    this.classDivision = state?.classDivision || '';
    this.className = this.classNumber ? `${this.classNumber}-${this.classDivision}` : '';

    this.loadStudentList();
  }

  private async loadStudentList() {
    if (!this.staffId || !this.classId) { this.fetchingStudentListData = false; return; }
    this.fetchingStudentListData = true;
    try {
      const response = await firstValueFrom(this.staffFeesOverdueReportService.getPendingStudentList(this.staffId, this.classId));
      if (response?.status && response.body) {
        const students = response.body.studentFeesOverdueData || [];
        this.activatedStudentData = students.filter(s => s.isBlocked == 0);
        this.deactivatedStudentData = students.filter(s => s.isBlocked == 1);
        this.totalActiveStudentCount = response.body.totalActiveStudentPendingCount || 0;
        this.totalDeactivateStudentCount = response.body.totalDeactiveStudentPendingCount || 0;
        this.totalActiveStudentPendingAmount = response.body.totalActiveStudentPendingAmount || 0;
        this.totalPendingAmount = response.body.totalPendingAmount || 0;
      } else {
        this.activatedStudentData = [];
        this.deactivatedStudentData = [];
      }
    } catch {
      this.activatedStudentData = [];
      this.deactivatedStudentData = [];
    } finally {
      this.fetchingStudentListData = false;
    }
  }

  setTab(tab: 'active' | 'deactivated') {
    if (this.activeTab === tab) { return; }
    this.activeTab = tab;
  }

  get students(): StudentFeeOverdueRecord[] {
    return this.activeTab === 'active' ? this.activatedStudentData : this.deactivatedStudentData;
  }

  get headerTotal(): number {
    return this.activeTab === 'active' ? this.totalActiveStudentPendingAmount : this.totalPendingAmount;
  }

  get headerTotalLabel(): string {
    return this.activeTab === 'active' ? 'totalExcludingSum' : 'totalIncludingSum';
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  pendingAmount(s: StudentFeeOverdueRecord): number {
    return s.feeDetails?.PendingAmount ?? 0;
  }

  showInfoPopover = false;

  toggleInfoPopover() {
    this.showInfoPopover = !this.showInfoPopover;
  }

  get infoMessageKey(): string {
    return this.activeTab === 'active' ? 'totalCollectionFromActive' : 'totalCollectionFromDeactivated';
  }

  goBack() {
    const state: StaffFeesReportNavState = { reportType: 'overdue' };
    this.router.navigate(['/staff/fees-report'], { state });
  }

  openStudent(s: StudentFeeOverdueRecord) {
    const state: StaffFeesReportOverdueStudentHistoryNavState = {
      studentName: s.studentName,
      className: this.className,
      classNumber: this.classNumber,
      classDivision: this.classDivision
    };
    this.router.navigate(['/staff/fees-overdue-report/class', this.classId, 'student', s.studentId], { state });
  }
}
